import { createFileRoute } from "@tanstack/react-router"
import { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { useState } from "react"
import { DialogTrigger } from "react-aria-components"

import { Button, IconButton, LinkButton } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Checkbox from "@/globalComponents/atoms/Checkbox"
import Description from "@/globalComponents/atoms/Description"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import Select from "@/globalComponents/molecules/Select"
import TextField from "@/globalComponents/molecules/TextField"
import VehicleSelectorField from "@/globalComponents/molecules/VehicleSelectorField"

export const Route = createFileRoute("/qr/$qrLookupID/dev/gut-check")({
  staticData: {
    pageName: "Gut Check - All Components",
  },
  component: RouteComponent,
})

function RouteComponent() {
  const [textValue, setTextValue] = useState("")
  const [selectValue, setSelectValue] = useState("")
  const [vehicleValue, setVehicleValue] = useState("")
  const [checkboxValue, setCheckboxValue] = useState(false)

  const vehicles: SavedPlate[] = [
    {
      plate: "ABC123",
      nickname: "My Honda",
      state: "CA",
      lastUsed: new Date().toISOString(),
    },
    {
      plate: "XYZ789",
      nickname: "",
      state: "NY",
      lastUsed: new Date().toISOString(),
    },
  ]

  return (
    <>
      <style>{`
        .gut-check-container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: var(--space-3);
          padding: var(--space-4);
        }
        .gut-check-section {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }
      `}</style>
      <div className="gut-check-container">
        {/* Typography */}
        <div className="gut-check-section">
          <Heading level={1}>Heading 1</Heading>
          <Heading level={2}>Heading 2</Heading>
          <Heading level={3}>Heading 3</Heading>
          <Heading level={4}>Heading 4</Heading>
          <Text level={1}>Body text level 1</Text>
          <Text level={2}>Body text level 2</Text>
          <Text level={3}>Body text level 3</Text>
          <Text level="disclaimer">Disclaimer text</Text>
        </div>

        {/* Buttons - Primary */}
        <div className="gut-check-section">
          <Button buttonType="primary" onPress={() => {}}>
            Primary Button
          </Button>
          <Button buttonType="primary" presentation="ghost" onPress={() => {}}>
            Primary Ghost
          </Button>
          <Button buttonType="primary" presentation="link" onPress={() => {}}>
            Primary Link
          </Button>
          <Button buttonType="primary" isDisabled onPress={() => {}}>
            Disabled
          </Button>
          <Button buttonType="primary" isPending onPress={() => {}}>
            Loading
          </Button>
          <Button buttonType="primary" onPress={() => {}}>
            <Icon name="parking" slot="left" />
            With Icon
          </Button>
        </div>

        {/* Buttons - Other Types */}
        <div className="gut-check-section">
          <Button buttonType="default" onPress={() => {}}>
            Default Button
          </Button>
          <Button buttonType="success" onPress={() => {}}>
            Success Button
          </Button>
          <Button buttonType="danger" onPress={() => {}}>
            Danger Button
          </Button>
          <Button buttonType="caution" onPress={() => {}}>
            Caution Button
          </Button>
          <IconButton
            buttonType="primary"
            iconName="close"
            onPress={() => {}}
            aria-label="Close"
          />
          <LinkButton
            buttonType="primary"
            to="/qr/$qrLookupID/dev"
            from="/qr/$qrLookupID/dev/gut-check"
          >
            Link Button
          </LinkButton>
        </div>

        {/* Icons */}
        <div className="gut-check-section">
          <div
            style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}
          >
            <Icon name="parking" />
            <Icon name="car" />
            <Icon name="calendar" />
            <Icon name="clock" />
            <Icon name="creditCard" />
            <Icon name="check" />
            <Icon name="close" />
            <Icon name="info" />
            <Icon name="alert" />
            <Icon name="person" />
            <Icon name="arrowRight" />
            <Icon name="chevronDown" />
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--space-2)",
              alignItems: "center",
            }}
          >
            <Icon name="parking" size="small" />
            <Icon name="parking" size="default" />
            <Icon name="parking" size="large" />
            <Icon name="parking" size="x-large" />
          </div>
        </div>

        {/* Cards - Neutral */}
        <div className="gut-check-section">
          <Card>
            <Heading level={3}>Neutral Card</Heading>
            <Text>Default card styling</Text>
          </Card>
          <Card hue="neutral" variant="solid">
            <Heading level={3}>Neutral Solid</Heading>
            <Text>Solid variant</Text>
          </Card>
        </div>

        {/* Cards - Colored */}
        <div className="gut-check-section">
          <Card hue="primary" variant="solid">
            <Text className="semi-bold">Primary Solid</Text>
          </Card>
          <Card hue="secondary" variant="solid">
            <Text className="semi-bold">Secondary Solid</Text>
          </Card>
          <Card hue="success" variant="solid">
            <Text className="semi-bold">Success Solid</Text>
          </Card>
          <Card hue="caution" variant="solid">
            <Text className="semi-bold">Caution Solid</Text>
          </Card>
          <Card hue="danger" variant="solid">
            <Text className="semi-bold">Danger Solid</Text>
          </Card>
        </div>

        {/* Text Fields */}
        <div className="gut-check-section">
          <TextField>
            <Label>Text Input</Label>
            <Description>Helper text</Description>
            <TextField.Input
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder="Enter text"
            />
          </TextField>
          <TextField isRequired>
            <Label>Required Field</Label>
            <TextField.Input placeholder="Required" />
          </TextField>
          <TextField isInvalid>
            <Label>Invalid Field</Label>
            <TextField.Input value="Invalid" />
            <FieldError>This field has an error</FieldError>
          </TextField>
          <TextField isDisabled>
            <Label>Disabled Field</Label>
            <TextField.Input value="Disabled" />
          </TextField>
        </div>

        {/* Special Text Fields */}
        <div className="gut-check-section">
          <TextField>
            <Label>Email</Label>
            <TextField.Input type="email" placeholder="email@example.com" />
          </TextField>
          <TextField>
            <Label>Password</Label>
            <TextField.Password innerRef={() => {}} />
          </TextField>
          <TextField>
            <Label>Phone</Label>
            <TextField.Phone innerRef={() => {}} />
          </TextField>
        </div>

        {/* Select */}
        <div className="gut-check-section">
          <Select
            value={selectValue}
            onChange={setSelectValue}
            placeholder="Select option"
          >
            <Label>Select Field</Label>
            <Select.Value />
            <Select.Popover>
              <Select.ListBox>
                <Select.ListBoxItem id="option1" textValue="Option 1">
                  Option 1
                </Select.ListBoxItem>
                <Select.ListBoxItem id="option2" textValue="Option 2">
                  Option 2
                </Select.ListBoxItem>
                <Select.ListBoxItem id="option3" textValue="Option 3">
                  Option 3
                </Select.ListBoxItem>
              </Select.ListBox>
            </Select.Popover>
          </Select>
          <Select disabled value="disabled" placeholder="Disabled">
            <Label>Disabled Select</Label>
            <Select.Value />
            <Select.Popover>
              <Select.ListBox>
                <Select.ListBoxItem id="disabled" textValue="Disabled">
                  Disabled
                </Select.ListBoxItem>
              </Select.ListBox>
            </Select.Popover>
          </Select>
        </div>

        {/* Checkbox */}
        <div className="gut-check-section">
          <Checkbox isSelected={checkboxValue} onChange={setCheckboxValue}>
            <Text>Checkbox label</Text>
          </Checkbox>
          <Checkbox isSelected={true} onChange={() => {}}>
            <Text>Checked checkbox</Text>
          </Checkbox>
          <Checkbox isDisabled>
            <Text>Disabled checkbox</Text>
          </Checkbox>
        </div>

        {/* Vehicle Selector */}
        <div className="gut-check-section">
          <VehicleSelectorField value={vehicleValue} onChange={setVehicleValue}>
            <Label>Select Vehicle</Label>
            {vehicles.map((vehicle) => (
              <VehicleSelectorField.Radio
                key={vehicle.plate}
                vehicle={vehicle}
              />
            ))}
            <VehicleSelectorField.Radio vehicle={undefined} />
          </VehicleSelectorField>
        </div>

        {/* Modals */}
        <div className="gut-check-section">
          <DialogTrigger>
            <Button buttonType="primary">Centered Modal</Button>
            <Modal>
              <Modal.Header>
                <Heading level={2} as={3}>
                  Modal Title
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>This is a centered modal with content.</Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <>
                    <Button
                      buttonType="default"
                      presentation="ghost"
                      onPress={state.close}
                    >
                      Cancel
                    </Button>
                    <Button buttonType="primary" onPress={state.close}>
                      Confirm
                    </Button>
                  </>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>

          <DialogTrigger>
            <Button buttonType="primary">Drawer Modal</Button>
            <Modal modalStyle="drawer">
              <Modal.Header>
                <Heading level={2} as={3}>
                  Drawer Modal
                </Heading>
              </Modal.Header>
              <Modal.Body>
                <Text>This is a drawer-style modal.</Text>
              </Modal.Body>
              <Modal.Footer>
                {(state) => (
                  <Button buttonType="primary" onPress={state.close}>
                    Close
                  </Button>
                )}
              </Modal.Footer>
            </Modal>
          </DialogTrigger>
        </div>

        {/* Menu */}
        <div className="gut-check-section">
          <Text>Menu component (MenuTrigger pattern)</Text>
        </div>

        {/* Separators */}
        <div className="gut-check-section">
          <Text>Horizontal Separator</Text>
          <Separator orientation="horizontal" />
          <Text>Content below separator</Text>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            <Text>Vertical</Text>
            <Separator orientation="vertical" />
            <Text>Separator</Text>
          </div>
        </div>

        {/* Nested Cards */}
        <div className="gut-check-section">
          <Card hue="primary" variant="solid">
            <Heading level={3}>Parent Card</Heading>
            <Text>This card contains a nested card</Text>
            <Card>
              <Text className="semi-bold">Nested Card</Text>
              <Text level={3}>Nested content</Text>
            </Card>
          </Card>
        </div>

        {/* Mixed Content */}
        <div className="gut-check-section">
          <Card>
            <Heading level={2}>Mixed Card</Heading>
            <Text>A card with multiple elements</Text>
            <Separator orientation="horizontal" />
            <Button buttonType="primary" onPress={() => {}}>
              <Icon name="check" slot="left" />
              Action Button
            </Button>
            <Button
              buttonType="default"
              presentation="ghost"
              onPress={() => {}}
            >
              Secondary Action
            </Button>
          </Card>
        </div>

        {/* All Button Types in One */}
        <div className="gut-check-section">
          <Card>
            <Heading level={3}>All Button Types</Heading>
            <Button buttonType="primary" onPress={() => {}}>
              Primary
            </Button>
            <Button buttonType="default" onPress={() => {}}>
              Default
            </Button>
            <Button buttonType="success" onPress={() => {}}>
              Success
            </Button>
            <Button buttonType="caution" onPress={() => {}}>
              Caution
            </Button>
            <Button buttonType="danger" onPress={() => {}}>
              Danger
            </Button>
          </Card>
        </div>

        {/* Form Example */}
        <div className="gut-check-section">
          <Card>
            <Heading level={3}>Quick Form</Heading>
            <TextField>
              <Label>Name</Label>
              <TextField.Input placeholder="Your name" />
            </TextField>
            <Select placeholder="Choose">
              <Label>Options</Label>
              <Select.Value />
              <Select.Popover>
                <Select.ListBox>
                  <Select.ListBoxItem id="a" textValue="A">
                    A
                  </Select.ListBoxItem>
                  <Select.ListBoxItem id="b" textValue="B">
                    B
                  </Select.ListBoxItem>
                </Select.ListBox>
              </Select.Popover>
            </Select>
            <Checkbox>
              <Text>I agree</Text>
            </Checkbox>
            <Button buttonType="primary" onPress={() => {}}>
              Submit
            </Button>
          </Card>
        </div>

        {/* Typography Hierarchy */}
        <div className="gut-check-section">
          <Card>
            <Heading level={1} as={2}>
              Page Title
            </Heading>
            <Text level={1}>
              Main body text with{" "}
              <Text as="span" className="semi-bold">
                bold text
              </Text>{" "}
              inline.
            </Text>
            <Heading level={2} as={3}>
              Section Heading
            </Heading>
            <Text level={2}>Secondary text for less important info.</Text>
            <Heading level={3} as={4}>
              Subsection
            </Heading>
            <Text level={3}>Tertiary text for captions and details.</Text>
          </Card>
        </div>

        {/* Icon Grid */}
        <div className="gut-check-section">
          <Card>
            <Heading level={3}>Common Icons</Heading>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "var(--space-2)",
              }}
            >
              <Icon name="home" />
              <Icon name="parking" />
              <Icon name="car" />
              <Icon name="person" />
              <Icon name="receipt" />
              <Icon name="ticket" />
              <Icon name="creditCard" />
              <Icon name="calendar" />
              <Icon name="clock" />
              <Icon name="check" />
              <Icon name="close" />
              <Icon name="info" />
            </div>
          </Card>
        </div>

        {/* Status Cards */}
        <div className="gut-check-section">
          <Card hue="success" variant="solid">
            <Icon name="check" />
            <Heading level={3}>Success</Heading>
            <Text>Operation completed</Text>
          </Card>
          <Card hue="caution" variant="solid">
            <Icon name="alert" />
            <Heading level={3}>Warning</Heading>
            <Text>Proceed with caution</Text>
          </Card>
          <Card hue="danger" variant="solid">
            <Icon name="close" />
            <Heading level={3}>Error</Heading>
            <Text>Something went wrong</Text>
          </Card>
        </div>
      </div>
    </>
  )
}
